/* Главная рамка «Кто говорит» (APP-SPEC-4 §3.1), кадрирование в ней (§3.5) и фрагмент своего видео (§3.6).
   Рамка — круг (кружок) или прямоугольник формата; когда всё готово к созданию, рамка «говорит» (пульс, класс talking).
   Бейдж цены за единицу — на нижней кромке рамки (A1), только из цены сервера. */
import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { uiFormat, uiText } from '../../../i18n';
import { StudioIcon } from '../StudioIcon';
import { mmss } from './order-tail-model';
import { circleFrame, type CircleCrop } from './circle-model';
import type { CircleMedia } from './CircleUpload';

export function CircleHero({
  format,
  wide = false,
  picture,
  name,
  sub,
  talking,
  empty,
  badge,
}: {
  format: string;
  wide?: boolean;
  picture: ReactNode;
  name: string;
  sub: string;
  talking: boolean;
  empty: boolean;
  badge?: ReactNode;
}) {
  const frame = circleFrame(format, wide);
  const rect = format !== 'circle';
  const vars = { '--w': `${frame.w}px`, '--h': `${frame.h}px`, '--sz': `${frame.w}px` } as CSSProperties;
  return (
    <div className={`ap-hero ls tc-hero${badge ? ' pb' : ''}`} id="apHero" style={vars}>
      <div className="ls-stage">
        <span
          className={`ci cur${rect ? ' rect' : ''}${rect && frame.w / frame.h > 1.3 ? ' wide' : ''}${talking ? ' talking' : ''}${empty ? ' empty' : ''}`}
          id="apBig"
          data-talking={talking ? 'true' : 'false'}
        >
          <span className="ring" id="apRing">
            {picture}
          </span>
        </span>
        {badge && (
          <span className="bc-pb tc-pb" id="apPb">
            {badge}
          </span>
        )}
      </div>
      <div className="who" id="apWho">
        <div className="nm" id="apName">
          {name}
        </div>
        <div className="sub" id="apSub">
          {sub}
        </div>
      </div>
    </div>
  );
}
/** Картинка в рамке: фото на всю рамку с кадром; без картинки — значок человека. */
export function CirclePicture({ url, video, crop }: { url?: string | null; video?: boolean; crop?: CircleCrop | null }) {
  const style = crop
    ? {
        objectPosition: `${(crop.cx * 100).toFixed(1)}% ${(crop.cy * 100).toFixed(1)}%`,
        transform: crop.z > 1 ? `scale(${crop.z})` : undefined,
        transformOrigin: `${(crop.cx * 100).toFixed(1)}% ${(crop.cy * 100).toFixed(1)}%`,
      }
    : undefined;
  if (!url)
    return (
      <span className="face tc-pic tc-none">
        <span className="ini">
          <StudioIcon kind="user" />
        </span>
      </span>
    );
  return <span className="face tc-pic">{video ? <video src={url} muted playsInline preload="metadata" style={style} /> : <img src={url} alt="" draggable={false} style={style} />}</span>;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
/** Кадрирование своего снимка или ролика в главной рамке: двигают пальцем или мышью, колесо и ползунок приближают (1–3). */
export function CircleCropper({
  format,
  media,
  value,
  onDone,
  onCancel,
}: {
  format: string;
  media: CircleMedia;
  value: CircleCrop | null;
  onDone(crop: CircleCrop): void;
  onCancel(): void;
}) {
  const [crop, setCrop] = useState<CircleCrop>(value ?? { cx: 0.5, cy: 0.5, z: 1 });
  const drag = useRef<{ x: number; y: number; cx: number; cy: number } | null>(null);
  const frame = circleFrame(format);
  const rect = format !== 'circle';
  const vars = { '--w': `${frame.w}px`, '--h': `${frame.h}px`, '--sz': `${frame.w}px` } as CSSProperties;
  const move = (dx: number, dy: number) => {
    const start = drag.current;
    if (!start) return;
    setCrop(current => ({
      ...current,
      cx: clamp(start.cx - dx / (frame.w * current.z), 0, 1),
      cy: clamp(start.cy - dy / (frame.h * current.z), 0, 1),
    }));
  };
  const style = {
    objectPosition: `${(crop.cx * 100).toFixed(1)}% ${(crop.cy * 100).toFixed(1)}%`,
    transform: `scale(${crop.z})`,
    transformOrigin: `${(crop.cx * 100).toFixed(1)}% ${(crop.cy * 100).toFixed(1)}%`,
  };
  return (
    <div className="ap-hero ls tc-hero circle-crop" id="apHero" style={vars}>
      <div className="ls-stage">
        <span className={`ci cur crop${rect ? ' rect' : ''}`} id="apBig">
          <span
            className="ring"
            id="apRing"
            role="img"
            aria-label={uiText('Кадр')}
            onPointerDown={event => {
              drag.current = { x: event.clientX, y: event.clientY, cx: crop.cx, cy: crop.cy };
              event.currentTarget.setPointerCapture?.(event.pointerId);
            }}
            onPointerMove={event => drag.current && move(event.clientX - drag.current.x, event.clientY - drag.current.y)}
            onPointerUp={() => (drag.current = null)}
            onPointerCancel={() => (drag.current = null)}
            onWheel={event => setCrop(current => ({ ...current, z: clamp(current.z * (event.deltaY < 0 ? 1.06 : 1 / 1.06), 1, 3) }))}
          >
            <span className="disc circle-crop-disc" id="cropBox">
              {media.kind === 'video' ? (
                <video src={media.url} muted playsInline preload="metadata" style={style} />
              ) : (
                <img id="cropImg" src={media.url} alt="" draggable={false} style={style} />
              )}
            </span>
          </span>
        </span>
      </div>
      <div className="ap-under" id="apUnder">
        <div className="czoom">
          <span className="zi">
            <StudioIcon kind="image" />
          </span>
          <input
            className="range"
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={crop.z}
            aria-label={uiText('Приближение')}
            style={{ ['--p' as string]: `${(((crop.z - 1) / 2) * 100).toFixed(1)}%` }}
            onChange={event => setCrop(current => ({ ...current, z: clamp(Number(event.target.value), 1, 3) }))}
          />
          <span className="zi big">
            <StudioIcon kind="image" />
          </span>
        </div>
        <p className="ap-hint">
          {uiText('Двигайте картинку, чтобы выбрать кадр')}
          {media.kind === 'video' ? ` · ${uiText('кадр применится ко всему видео')}` : ''}
        </p>
        <div className="ap-acts">
          <button type="button" className="btn" onClick={() => onDone(crop)}>
            <StudioIcon kind="check" />
            {uiText('Готово')}
          </button>
          <button type="button" className="btn line" onClick={onCancel}>
            <StudioIcon kind="close" />
            {uiText('Отмена')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Фрагмент своего видео (BotCore.trim): окно до предела (минута) едет по ролику; длина окна — длина результата у своей речи. */
export function CircleTrim({
  durationMs,
  maxMs,
  value,
  disabled,
  onChange,
}: {
  durationMs: number;
  maxMs: number;
  value: { start: number; end: number } | null;
  disabled: boolean;
  onChange(value: { start: number; end: number }): void;
}) {
  const windowMs = Math.min(durationMs, maxMs);
  const start = clamp(value?.start ?? 0, 0, Math.max(0, durationMs - windowMs));
  const end = Math.min(durationMs, start + windowMs);
  return (
    <div className="ap-trim circle-trim" id="apTrim">
      <div className="circle-trim-h">
        <b>{uiText('Фрагмент')}</b>
        <span className="mono">
          {mmss(start / 1000)}–{mmss(end / 1000)}
        </span>
        <small>{uiFormat('максимум минута · до {0}', mmss(maxMs / 1000))}</small>
      </div>
      <input
        type="range"
        className="range"
        min={0}
        max={Math.max(0, durationMs - windowMs)}
        step={100}
        value={start}
        disabled={disabled || durationMs <= windowMs}
        aria-label={uiText('Начало фрагмента')}
        onChange={event => {
          const next = Number(event.target.value);
          onChange({ start: next, end: Math.min(durationMs, next + windowMs) });
        }}
      />
    </div>
  );
}
